/* eslint-disable no-unused-vars */
import { useId, useEffect, useMemo, useCallback } from 'react';
import * as S from './ExcludeRulesSection.styled';
import { useDispatch, useSelector } from 'react-redux';
import { Message, ExcludeSettingsForm, Loader } from 'components';
import { manageCampaignsAPI } from 'services/manageCampaigns.service';
import { campaignsManageAPI } from 'services/campaignsManage.service';
import { useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { targetingActions } from 'store/actions';

export const ExcludeRulesSection = () => {
  const { cmp_id } = useParams();
  const formId = useId();
  const dispatch = useDispatch();

  const { data, isLoading, isSuccess } =
    manageCampaignsAPI.useGetCampaignAdvancedSettingsQuery(cmp_id);
  const { data: globalSettings } = campaignsManageAPI.useGetGlobalCampaignAdvancedSettingsQuery();

  const excluded_apps = useMemo(() => {
    const global_apps = globalSettings?.excluded_apps;
    const local_apps = data?.excluded_apps;
    const res = [];
    if (global_apps && local_apps) {
      const uniqueIds = Array.from(
        new Set([...global_apps, ...local_apps].map(({ pba_number }) => pba_number)),
      );
      uniqueIds.forEach((uniqueId) => {
        const global_app = global_apps.find(({ pba_number }) => pba_number === uniqueId);
        const local_app = local_apps.find(({ pba_number }) => pba_number === uniqueId);
        const new_app = {
          ...global_app,
          ...local_app,
        };
        res.push(new_app);
      });
      return res;
    }
  }, [globalSettings?.excluded_apps, data?.excluded_apps]);

  const excluded_publishers = useMemo(() => {
    const global_publishers = globalSettings?.excluded_publishers;
    const local_publishers = data?.excluded_publishers;
    const res = [];
    if (global_publishers && local_publishers) {
      const uniqueIds = Array.from(
        new Set([...global_publishers, ...local_publishers].map(({ pb_number }) => pb_number)),
      );
      uniqueIds.forEach((uniqueId) => {
        const global_publisher = global_publishers.find(({ pb_number }) => pb_number === uniqueId);
        const local_publisher = local_publishers.find(({ pb_number }) => pb_number === uniqueId);
        const new_publisher = {
          ...global_publisher,
          ...local_publisher,
        };
        res.push(new_publisher);
      });
      return res;
    }
  }, [globalSettings?.excluded_publishers, data?.excluded_publishers]);

  const globalExcTypes = useMemo(
    () => globalSettings?.typeList.map(({ value }) => +value),
    [globalSettings?.typeList],
  );

  const mixedExcTypes = useMemo(() => {
    const res = data?.typeList?.map((typeData) => ({
      ...typeData,
      isGlobal: globalExcTypes?.includes(+typeData.value),
    }));
    return res;
  }, [globalExcTypes, data?.typeList]);

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm({
    defaultValue: data,
    mode: 'onChange',
  });

  useEffect(() => {
    mixedExcTypes && setValue('typeList', mixedExcTypes);
  }, [mixedExcTypes]);

  useEffect(() => {
    excluded_apps && setValue('excluded_apps', excluded_apps);
  }, [excluded_apps]);

  useEffect(() => {
    excluded_publishers && setValue('excluded_publishers', excluded_publishers);
  }, [excluded_publishers]);

  const onSubmit = async (data) => {
    console.log(data);
  };

  useEffect(() => {
    if (isSuccess && data && isDirty) {
      dispatch(targetingActions.setDefaultExcludeRules(watch()));
    }
  }, [watch(), isSuccess, data, isDirty]);

  const clearExcludedSection = useCallback(
    (key) => {
      let newValue = [];
      if (key === 'excluded_publishers') newValue = globalSettings?.excluded_publishers || [];
      if (key === 'excluded_apps') newValue = globalSettings?.excluded_apps || [];

      setValue(key, newValue);
    },
    [globalSettings],
  );

  if (isLoading)
    return (
      <S.LoaderBox>
        <Loader.Local />
      </S.LoaderBox>
    );

  return (
    <>
      {data && (
        <S.FormContainer onSubmit={handleSubmit(onSubmit)} id={formId}>
          <Message type={Message.messageType.information} isOpen={true}>
            General opt-out rules apply to ALL campaigns in your portfolio. You can edit them on
            Advanced Settings.
          </Message>
          <S.SectionWrap>
            <Controller
              name='typeList'
              control={control}
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <ExcludeSettingsForm.ExcludeCategories
                  id='typeList'
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </S.SectionWrap>
          <S.SectionWrap>
            <Controller
              defaultValue={excluded_publishers || []}
              name='excluded_publishers'
              control={control}
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <ExcludeSettingsForm.ExcludePublishers
                  id='excluded_publishers'
                  value={value}
                  onChange={onChange}
                  onClear={clearExcludedSection}
                />
              )}
            />
          </S.SectionWrap>
          <S.SectionWrap>
            <Controller
              defaultValue={excluded_apps || []}
              name='excluded_apps'
              control={control}
              render={({ field: { onChange, value }, formState: { errors } }) => (
                <ExcludeSettingsForm.ExcludeApps
                  fullWidth={false}
                  sectionName='Exclude Apps:'
                  id='excluded_apps'
                  value={value}
                  onChange={onChange}
                  onClear={clearExcludedSection}
                />
              )}
            />
          </S.SectionWrap>
        </S.FormContainer>
      )}
    </>
  );
};
